// One complete content surface; numeric/plot entrances use their own controller.
// Live DOM only fades: translating its ancestor would trap fixed/sticky UI.
// The matching 6px movement belongs to browser snapshots in workspace motion.
export const SURFACE_MOTION = { duration: 220, easing: 'cubic-bezier(.22, .75, .25, 1)' };
import { mountDataSurface, replayDataSurface, unmountDataSurface } from './data-motion.js';
const surfaces = new WeakMap();
export function createSurfaceMotion(element, media = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')) {
  let animation, epoch = 0, pending = false, destroyed = false;
  element?.setAttribute?.('data-unified-surface', '');
  const cancel = () => {
    epoch++; pending = false;
    animation?.cancel();
    animation = undefined;
    element?.removeAttribute?.('data-surface-entering');
  };
  const onPreferenceChange = () => { if (media?.matches) cancel(); };
  media?.addEventListener?.('change', onPreferenceChange);
  return {
    get running() { return pending || Boolean(animation); },
    play() {
      cancel();
      if (destroyed || media?.matches || !element?.animate) return;
      pending = true;
      const intent = epoch;
      element?.setAttribute?.('data-surface-entering', '');
      // Vue mounts children before parents. Batch one render's requests so a
      // parent owns the whole entrance instead of multiplying nested fades.
      queueMicrotask(() => {
        if (destroyed || intent !== epoch) return;
        const doc = element.ownerDocument;
        const parentEntering = element.parentElement?.closest?.('[data-surface-entering],.motion-modal-enter-active,.motion-popover-enter-active');
        if (media?.matches || doc?.hidden || parentEntering
          || doc?.documentElement.hasAttribute('data-workspace-transition')
          || element.checkVisibility?.({ checkVisibilityCSS: true }) === false) { cancel(); return; }
        pending = false;
        animation = element.animate([{ opacity: 0 }, { opacity: 1 }], SURFACE_MOTION);
        animation.id = 'nur-surface-enter';
        const current = animation;
        current.onfinish = () => { if (animation === current) cancel(); };
      });
    },
    destroy() {
      destroyed = true;
      cancel();
      media?.removeEventListener?.('change', onPreferenceChange);
      element?.removeAttribute?.('data-unified-surface');
    }
  };
}

// A local workspace update may not change the parent's route key (Budget,
// Vision). Reuse its registered boundary in browsers without snapshots. Never
// attach a global mutation observer or animate each incoming API payload.
export function replaySurface(element) {
  const boundary = surfaces.has(element) ? element : element?.closest?.('[data-unified-surface]');
  const controller = boundary && surfaces.get(boundary);
  if (controller && !controller.running) controller.play();
  if (element) replayDataSurface(element);
}
export function suspendLeavingDialog(element) {
  (element.matches?.('[role="dialog"]') ? element : element.querySelector('[role="dialog"]'))?.setAttribute('inert', '');
}
export function resumeEnteringDialog(element) {
  (element.matches?.('[role="dialog"]') ? element : element.querySelector('[role="dialog"]'))?.removeAttribute('inert');
}
export const surfaceMotion = {
  mounted(element) {
    const controller = createSurfaceMotion(element);
    surfaces.set(element, controller);
    controller.play();
    mountDataSurface(element);
  },
  updated(element, binding) {
    if (binding.value !== binding.oldValue) {
      surfaces.get(element)?.play();
      replayDataSurface(element);
    }
  },
  unmounted(element) {
    unmountDataSurface(element);
    surfaces.get(element)?.destroy();
    surfaces.delete(element);
  }
};
